import * as React from "react";
import HoldUtilHeader from "../../generic/holdUtilHeader";
import HoldEqUsableRowDsk from "./holdEqUsableRowDsk";
import HoldEqUsableRowMob from "./holdEqUsableRowMob";
import DeviceContext from "../../../../service/indentifyDevice";
import { HHoldMTFGroupBy } from "../../../../service/holdings/holdGrpByHelper";
import Sort, { eSortState, sort } from "../../../../generic/sort";
import { SearchHighLight } from "../../../../service/generic/searchHighlight";
import NoDataContainer from "../../../../generic/noDataContainer";
import { initWS } from "../../../../service/edelWebSockets/wsCallbacks";
import { ISocketSubscribeReq } from "../../../../types/components/edelSockets/edelSocketTypes";
import { edelSocketQuoteReq } from "../../../../service/edelWebSockets/wsInit";
import { getAccData } from "../../../../service/login/loginService";
import { genHshObj } from "../../../../utils/hashObj";

if (DeviceContext().isDesktop) {
  require("../../../../../assets/scss/components/toc/holdings/holdTables/holdTableDsk.scss");
} else if (DeviceContext().isTablet) {
  require("../../../../../assets/scss/components/toc/holdings/holdTables/holdTableTab.scss");
} else {
  require("../../../../../assets/scss/components/toc/holdings/holdTables/holdTableMob.scss");
}

export interface IHoldEqUsableTableProps {
  data: Array<any>;
}

export interface IHoldEqUsableTableState {
  toggleState: boolean;
  initialData: Array<any>;
  filteredData: Array<Array<any>>;
  tableData: Array<Array<any>>;
  sortedColumnState: { colName: string | null; sortState: eSortState };
}

const heading = {
  trdSym: "Symbol",
  qty: "Normal Qty",
  t1HQty: "T1 Qty",
  usdQty: "Used Qty",
  totQty: "Total Normal Qty",
  ltp: "LTP/Close",
  hdgVl: "Normal Holding Value",
};

class HoldEqUsableTable extends React.Component<
  IHoldEqUsableTableProps,
  IHoldEqUsableTableState
> {
  state: IHoldEqUsableTableState = {
    toggleState: false,
    initialData: this.props.data,
    filteredData: [this.props.data],
    tableData: [this.props.data],
    sortedColumnState: { colName: null, sortState: eSortState.ASC },
  };

  filteredHash: { [x: string]: [number, number] } = {}; // hash object for filteredData
  tableHash: { [x: string]: [number, number] } = {}; // hash object for tableData

  parsePropData = () => {
    // adds hdgVl on the first level, just for sorting; DON"T USE IT FOR DATA BINDING
    return this.state.initialData.map((row) => ({
      ...row,
      hdgVl: row.cncRmsHdg ? row.cncRmsHdg.hdgVl : row.mtfRmsHdg.hdgVl,
    }));
  };

  componentDidMount() {
    initWS<ISocketSubscribeReq>(
      edelSocketQuoteReq("quote3", {
        vendID: "EDEL",
        vendToken: "",
        userID: getAccData("userID"),
        accType: getAccData("accTyp"),
        symbols: this.props.data.map((data) => ({ symbol: data.sym })),
      }),
      this.socketMessages
    );

    // parse data first to move hdgVl to first level
    const data = this.parsePropData();

    if (this.state.toggleState) {
      // when data comes initially, group it
      const groupedData = HHoldMTFGroupBy(data);
      this.setState({
        filteredData: groupedData,
        tableData: groupedData,
        initialData: data,
      });

      this.filteredHash = genHshObj(groupedData, "sym");
      this.tableHash = this.filteredHash;
    } else {
      this.setState({
        filteredData: [data],
        tableData: [data],
        initialData: data,
      });

      this.filteredHash = genHshObj([data], "sym");
      this.tableHash = this.filteredHash;
    }
  }

  componentWillUnmount = () => {
    initWS<ISocketSubscribeReq>(
      edelSocketQuoteReq(
        "quote3",
        {
          vendID: "EDEL",
          vendToken: "",
          userID: getAccData("userID"),
          accType: getAccData("accTyp"),
          symbols: this.props.data.map((data) => ({ symbol: data.sym })),
        },
        "unsubscribe"
      ),
      () => {}
    );
  };

  checkMTFHoldings = () => {
    // chack if there is any MTF holding in data
    return !!this.props.data.find((data) => data.mtfRmsHdg);
  };

  isMtfHoldingsPresent = this.checkMTFHoldings();

  onSort = (
    data: Array<Array<any>>,
    sortState: eSortState,
    colName: string
  ) => {
    this.setState({
      sortedColumnState: { colName, sortState },
      tableData: data,
    });
    this.tableHash = genHshObj(data, "sym");
  };

  sortAfterFilter = (groupedData: Array<Array<any>>) => {
    // if some column was sorted earlier, then sort that again
    if (this.state.sortedColumnState.colName) {
      // copy grouped data, because it will change after sorting
      // filtered data will also change with groupedData
      const copyGroupedData = groupedData.map((group) =>
        group.map((data) => data)
      );

      sort(
        copyGroupedData,
        this.state.sortedColumnState.sortState,
        this.state.sortedColumnState.colName,
        this.onSort
      );
    }
  };

  handleToggleClick = (
    groupedData: Array<Array<any>>,
    toggleState: boolean
  ) => {
    this.setState({
      toggleState: toggleState,
      filteredData: groupedData,
      tableData: groupedData,
    });
    this.filteredHash = genHshObj(groupedData, "sym");
    this.tableHash = this.filteredHash;

    this.sortAfterFilter(groupedData);
  };

  handleSearch = (filteredData: Array<Array<any>>, sText?: string) => {
    if (sText) {
      const SearchText: string = sText.toLowerCase();
      const searchKey: string = "trdSym";

      // highlight the rows
      filteredData[0] = filteredData[0].map(({ ...row }: any) => ({
        ...row,
        [searchKey]: SearchHighLight(row[searchKey], SearchText),
      }));
    }

    // group data to pass into handle toggle click
    let groupedData: Array<Array<any>> = [];
    if (this.state.toggleState) {
      groupedData = HHoldMTFGroupBy(filteredData[0]);
    } else {
      groupedData = filteredData;
    }

    this.handleToggleClick(groupedData, this.state.toggleState);
  };

  socketMessages = (res: any) => {
    if (typeof res === "string") {
      // success
      const { response } = JSON.parse(res);
      const sym = response.data.z3;
      const ltp = response.data.a9;

      const filteredIndex = this.filteredHash[sym];
      const tableIndex = this.tableHash[sym];

      if (tableIndex)
        console.log(ltp, this.state.tableData[tableIndex[0]][tableIndex[1]]);

      if (
        filteredIndex &&
        tableIndex &&
        this.state.tableData[tableIndex[0]][tableIndex[1]].ltp !== ltp
      ) {
        // console.log(this.state.tableData[tableIndex[0]][tableIndex[1]], ltp);s
        const filteredDataTemp = this.state.filteredData;
        const tableDataTemp = this.state.tableData;

        let row = {
          ...this.state.filteredData[filteredIndex[0]][filteredIndex[1]],
        };
        this.updateRowOnStream(row, ltp);
        filteredDataTemp[filteredIndex[0]][filteredIndex[1]] = row;

        row = { ...this.state.tableData[tableIndex[0]][tableIndex[1]] };
        this.updateRowOnStream(row, ltp);
        tableDataTemp[tableIndex[0]][tableIndex[1]] = row;

        this.setState({
          filteredData: filteredDataTemp,
          tableData: tableDataTemp,
        });
      }
    } else {
      //console.log(res.type);
    }
  };

  updateRowOnStream(row: any, ltp: string) {
    row.ltp = ltp;
    if (row.cncRmsHdg.hdgVl) row.cncRmsHdg.hdgVl = +ltp * row.cncRmsHdg.totQty;
    if (row.cncRmsHdg.hdgVl) row.cncRmsHdg.hdgVl = +ltp * row.cncRmsHdg.totQty;
  }

  render() {
    return (
      <>
        <HoldUtilHeader
          data={[this.state.initialData || []]}
          searchKey="trdSym"
          onDataFilter={this.handleSearch}
          onToggleClick={this.handleToggleClick}
          onSearchClick={() => {}}
          defaultToggleState={
            this.isMtfHoldingsPresent ? this.state.toggleState : undefined
          }
        />

        <div className="hold_eq_usable_table_dsk hold_table">
          {!DeviceContext().isMobile && (
            <ul className="hdr_row">
              <li>
                <div className="col trdSym">
                  <Sort
                    sortList={this.state.filteredData}
                    sortedColName={this.state.sortedColumnState.colName}
                    sortKey="trdSym"
                    onSort={this.onSort}
                    colName={heading.trdSym}
                  />
                </div>
                <div className="col qty w_80">{heading.qty}</div>
                <div className="col t1HQty w_80">{heading.t1HQty}</div>
                <div className="col usdQty w_80">{heading.usdQty}</div>
                <div className="col totQty w_80">{heading.totQty}</div>
                <div className="col ltp w_80">{heading.ltp}</div>
                <div className="col hdgVl w_90">
                  <Sort
                    sortList={this.state.filteredData}
                    sortedColName={this.state.sortedColumnState.colName}
                    sortKey="hdgVl"
                    onSort={this.onSort}
                    colName={heading.hdgVl}
                  />
                </div>
              </li>
            </ul>
          )}

          {/* Flatten the array and check if there is anything to be shown */}
          {this.state.tableData.join().split(",")[0] ? (
            <div className="hold_scroll_cont">
              {this.state.tableData.map((group, groupIndex) => (
                <ul key={groupIndex}>
                  {group.map((row, index) => (
                    <React.Fragment key={index}>
                      {!DeviceContext().isMobile ? (
                        <HoldEqUsableRowDsk rowData={row} />
                      ) : (
                        <HoldEqUsableRowMob rowData={row} />
                      )}
                    </React.Fragment>
                  ))}
                </ul>
              ))}
            </div>
          ) : (
            <NoDataContainer txtHeading="No results found" />
          )}
        </div>
      </>
    );
  }
}

export default HoldEqUsableTable;
